import { createStyles } from '@udecode/plate-styled-components';
import _styled, { css } from 'styled-components';
import React from 'react';
import { Box, usePlateEditorRef } from '@udecode/plate-core';
import { Image, insertImage } from '@udecode/plate-image';
import { useFocused, useSelected, useReadOnly } from 'slate-react';
import { ToolbarButton } from '@udecode/plate-ui-toolbar';

const getImageElementStyles = props => {
  const {
    focused,
    selected,
    align,
    caption = {
      align: 'center'
    }
  } = props;
  const handle = [{
    "display": "flex",
    "flexDirection": "column",
    "justifyContent": "center",
    "position": "absolute",
    "userSelect": "none"
  }, {
    "width": "1.5rem",
    "height": "100%",
    "top": "0px",
    "zIndex": "10"
  }, css(["::after{", ";", ";", ";", ";", ";content:' ';width:3px;height:64px;border-radius:6px;}:hover,:focus,:active{::after{", ";}}"], {
    "opacity": "0"
  }, focused && selected && {
    "opacity": "1"
  }, {
    ".group:hover &": {
      "opacity": "1"
    }
  }, {
    "display": "flex"
  }, {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgba(156, 163, 175, var(--tw-bg-opacity))"
  }, {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgba(59, 130, 246, var(--tw-bg-opacity))"
  })];
  return createStyles({
    prefixClassNames: 'ImageElement',
    ...props
  }, {
    root: [{
      "paddingTop": "0.625rem",
      "paddingBottom": "0.625rem"
    }],
    resizable: [align === 'center' && {
      "marginLeft": "auto",
      "marginRight": "auto"
    }, align === 'right' && {
      "marginLeft": "auto"
    }],
    figure: [{
      "margin": "0px",
      "position": "relative"
    }],
    img: [{
      "display": "block",
      "maxWidth": "100%",
      "paddingLeft": "0px",
      "paddingRight": "0px",
      "cursor": "pointer",
      "width": "100%"
    }, {
      "borderRadius": "3px",
      "objectFit": "cover"
    }, focused && selected && {
      "boxShadow": "0 0 0 1px rgb(59,130,249)"
    }],
    figcaption: [align === 'center' && {
      "marginLeft": "auto",
      "marginRight": "auto"
    }, align === 'right' && {
      "marginLeft": "auto"
    }],
    caption: [{
      "width": "100%",
      "borderStyle": "none",
      ":focus": {
        "outline": "2px solid transparent",
        "outlineOffset": "2px"
      },
      "marginTop": "0.5rem",
      "padding": "0px",
      "resize": "none"
    }, (caption === null || caption === void 0 ? void 0 : caption.align) === 'center' && {
      "textAlign": "center"
    }, (caption === null || caption === void 0 ? void 0 : caption.align) === 'right' && {
      "textAlign": "right"
    }, css(["font:inherit;color:inherit;background-color:inherit;:focus{::placeholder{opacity:0;}}"])],
    handleLeft: [...handle, {
      "left": "-0.75rem",
      "marginLeft": "-0.75rem",
      "paddingLeft": "0.75rem"
    }],
    handleRight: [...handle, {
      "alignItems": "flex-end",
      "right": "-0.75rem",
      "marginRight": "-0.75rem",
      "paddingRight": "0.75rem"
    }]
  });
};

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

const ImageElement = props => {
  var _styles$figure, _styles$figure2, _styles$resizable, _styles$handleLeft, _styles$handleLeft2, _styles$handleRight, _styles$handleRight2, _styles$resizable2, _styles$img, _styles$img2, _styles$figcaption, _styles$figcaption2, _styles$caption, _caption$placeholder, _styles$caption2;

  const {
    children,
    nodeProps,
    caption = {},
    resizableProps,
    align = 'center',
    ignoreReadOnly = false
  } = props;
  const {
    as,
    ...rootProps
  } = props;
  const focused = useFocused();
  const selected = useSelected();
  const readOnly = useReadOnly();
  const styles = getImageElementStyles({ ...props,
    align,
    focused,
    selected
  });
  return /*#__PURE__*/React.createElement(_StyledImageRoot, _extends({}, rootProps, {
    $_css: styles.root.css
  }), /*#__PURE__*/React.createElement(_StyledFigure, {
    className: `group ${(_styles$figure = styles.figure) === null || _styles$figure === void 0 ? void 0 : _styles$figure.className}`,
    contentEditable: false,
    $_css2: (_styles$figure2 = styles.figure) === null || _styles$figure2 === void 0 ? void 0 : _styles$figure2.css
  }, /*#__PURE__*/React.createElement(_StyledImageResizable, _extends({
    className: (_styles$resizable = styles.resizable) === null || _styles$resizable === void 0 ? void 0 : _styles$resizable.className,
    handleComponent: {
      left: /*#__PURE__*/React.createElement(_StyledBox, {
        className: (_styles$handleLeft = styles.handleLeft) === null || _styles$handleLeft === void 0 ? void 0 : _styles$handleLeft.className,
        $_css4: [(_styles$handleLeft2 = styles.handleLeft) === null || _styles$handleLeft2 === void 0 ? void 0 : _styles$handleLeft2.css]
      }),
      right: /*#__PURE__*/React.createElement(_StyledBox2, {
        className: (_styles$handleRight = styles.handleRight) === null || _styles$handleRight === void 0 ? void 0 : _styles$handleRight.className,
        $_css5: (_styles$handleRight2 = styles.handleRight) === null || _styles$handleRight2 === void 0 ? void 0 : _styles$handleRight2.css
      })
    },
    align: align,
    readOnly: !ignoreReadOnly && readOnly
  }, resizableProps, {
    $_css3: (_styles$resizable2 = styles.resizable) === null || _styles$resizable2 === void 0 ? void 0 : _styles$resizable2.css
  }), /*#__PURE__*/React.createElement(_StyledImageImg, _extends({
    className: (_styles$img = styles.img) === null || _styles$img === void 0 ? void 0 : _styles$img.className
  }, nodeProps, {
    $_css6: (_styles$img2 = styles.img) === null || _styles$img2 === void 0 ? void 0 : _styles$img2.css
  }))), !caption.disabled && /*#__PURE__*/React.createElement(_StyledImageCaption, {
    className: (_styles$figcaption = styles.figcaption) === null || _styles$figcaption === void 0 ? void 0 : _styles$figcaption.className,
    $_css7: (_styles$figcaption2 = styles.figcaption) === null || _styles$figcaption2 === void 0 ? void 0 : _styles$figcaption2.css
  }, /*#__PURE__*/React.createElement(_StyledImageCaptionTextarea, {
    className: (_styles$caption = styles.caption) === null || _styles$caption === void 0 ? void 0 : _styles$caption.className,
    placeholder: (_caption$placeholder = caption.placeholder) !== null && _caption$placeholder !== void 0 ? _caption$placeholder : 'Write a caption...',
    readOnly: !ignoreReadOnly && readOnly || !!caption.readOnly,
    $_css8: (_styles$caption2 = styles.caption) === null || _styles$caption2 === void 0 ? void 0 : _styles$caption2.css
  }))), children);
};

var _StyledImageRoot = _styled(Image.Root).withConfig({
  displayName: "ImageElement___StyledImageRoot",
  componentId: "sc-1j9k8xn-0"
})(["", ""], p => p.$_css);

var _StyledFigure = _styled("figure").withConfig({
  displayName: "ImageElement___StyledFigure",
  componentId: "sc-1j9k8xn-1"
})(["", ""], p => p.$_css2);

var _StyledImageResizable = _styled(Image.Resizable).withConfig({
  displayName: "ImageElement___StyledImageResizable",
  componentId: "sc-1j9k8xn-2"
})(["", ""], p => p.$_css3);

var _StyledBox = _styled(Box).withConfig({
  displayName: "ImageElement___StyledBox",
  componentId: "sc-1j9k8xn-3"
})(["", ""], p => p.$_css4);

var _StyledBox2 = _styled(Box).withConfig({
  displayName: "ImageElement___StyledBox2",
  componentId: "sc-1j9k8xn-4"
})(["", ""], p => p.$_css5);

var _StyledImageImg = _styled(Image.Img).withConfig({
  displayName: "ImageElement___StyledImageImg",
  componentId: "sc-1j9k8xn-5"
})(["", ""], p => p.$_css6);

var _StyledImageCaption = _styled(Image.Caption).withConfig({
  displayName: "ImageElement___StyledImageCaption",
  componentId: "sc-1j9k8xn-6"
})(["", ""], p => p.$_css7);

var _StyledImageCaptionTextarea = _styled(Image.CaptionTextarea).withConfig({
  displayName: "ImageElement___StyledImageCaptionTextarea",
  componentId: "sc-1j9k8xn-7"
})(["", ""], p => p.$_css8);

const ImageToolbarButton = ({
  id,
  getImageUrl,
  ...props
}) => {
  const editor = usePlateEditorRef(id);
  return /*#__PURE__*/React.createElement(ToolbarButton, _extends({
    onMouseDown: async event => {
      if (!editor) return;
      event.preventDefault();
      let url;

      if (getImageUrl) {
        url = await getImageUrl();
      } else {
        url = window.prompt('Enter the URL of the image:');
      }

      if (!url) return;
      insertImage(editor, url);
    }
  }, props));
};

export { ImageElement, ImageToolbarButton, getImageElementStyles };
//# sourceMappingURL=index.es.js.map
